/**
 * Telemetry Event Schema
 * 
 * Gate D Resolution: Formal event taxonomy for Loop Orienteering telemetry
 * TypeScript types and validation for all canonical events.
 * 
 * Event Categories:
 * - Loop Lifecycle (loop.*)
 * - Event Lifecycle (event.*)
 * - Admin Actions (admin.*)
 * - Authentication (auth.*)
 * - Debug (debug.*)
 */

/**
 * Base event properties included in all events
 */
export interface BaseEventProperties {
  timestamp?: number;
  platform?: 'ios' | 'android' | 'web';
  [key: string]: any;
}

// ============================================================================
// Loop Lifecycle Events
// ============================================================================

export type RunType = 'classic' | 'points' | 'hash' | 'event';
export type LoopEventType = 'public' | 'private';

/**
 * loop.start - User starts a new loop run
 */
export interface LoopStartEvent extends BaseEventProperties {
  runType: RunType;
  loopId: string;
  runId: string;
  eventId?: string;
  loopName?: string;
  eventType?: LoopEventType;
}

/**
 * loop.checkpoint - User reaches a checkpoint (points loops only)
 */
export interface LoopCheckpointEvent extends BaseEventProperties {
  runType: 'points';
  loopId: string;
  runId: string;
  checkpointId: string;
  sequenceNumber: number;
  timeSinceStart?: number;
  totalCheckpoints?: number;
}

/**
 * loop.finish - User completes a loop
 */
export interface LoopFinishEvent extends BaseEventProperties {
  runType: RunType;
  loopId: string;
  runId: string;
  totalTime: number;
  distance: number;
  eventId?: string;
  returnedToStart?: boolean;
}

/**
 * loop.abandon - User abandons an active loop
 */
export interface LoopAbandonEvent extends BaseEventProperties {
  runType: RunType;
  loopId: string;
  runId: string;
  abandonedAt: number;
  reason?: 'user_quit' | 'gps_error' | 'app_crash';
}

/**
 * loop.resume - User resumes a paused loop (future)
 */
export interface LoopResumeEvent extends BaseEventProperties {
  runType: RunType;
  loopId: string;
  runId: string;
  pausedDuration: number;
}

// ============================================================================
// Event Lifecycle Events
// ============================================================================

/**
 * event.create - User creates a public/private event
 */
export interface EventCreateEvent extends BaseEventProperties {
  eventType: 'public' | 'private';
  eventId: string;
  loopId: string;
  loopType: RunType;
  maxParticipants?: number;
  eventDate?: string;
}

/**
 * event.join - User joins an event
 */
export interface EventJoinEvent extends BaseEventProperties {
  eventType: 'public' | 'private';
  eventId: string;
  joinMethod: 'browse' | 'code';
  participantCount?: number;
}

/**
 * event.leave - User leaves an event (before it starts)
 */
export interface EventLeaveEvent extends BaseEventProperties {
  eventType: 'public' | 'private';
  eventId: string;
}

/**
 * event.complete - User completes an event run
 */
export interface EventCompleteEvent extends BaseEventProperties {
  eventType: 'public' | 'private';
  eventId: string;
  runId: string;
  placement?: number;
  totalParticipants?: number;
  completionTime?: number;
}

// ============================================================================
// Admin Action Events
// ============================================================================

/**
 * admin.loop.create - Admin creates a new loop
 */
export interface AdminLoopCreateEvent extends BaseEventProperties {
  loopType: 'classic' | 'points';
  loopId: string;
  checkpointCount?: number;
}

/**
 * admin.loop.update - Admin modifies an existing loop
 */
export interface AdminLoopUpdateEvent extends BaseEventProperties {
  loopType: RunType;
  loopId: string;
  changes: string[];
}

/**
 * admin.loop.delete - Admin soft-deletes a loop
 */
export interface AdminLoopDeleteEvent extends BaseEventProperties {
  loopType: RunType;
  loopId: string;
}

/**
 * admin.user.set_role - Admin changes user role
 */
export interface AdminUserSetRoleEvent extends BaseEventProperties {
  targetUserId: string; // Should be hashed
  newRole: 'admin' | 'user';
}

/**
 * admin.event.finalize - Admin finalizes an event
 */
export interface AdminEventFinalizeEvent extends BaseEventProperties {
  eventId: string;
  participantCount: number;
}

// ============================================================================
// Authentication Events
// ============================================================================

/**
 * auth.sign_in - User signs in
 */
export interface AuthSignInEvent extends BaseEventProperties {
  platform: 'ios' | 'android' | 'web';
  success: boolean;
  method?: 'email' | 'oauth';
}

/**
 * auth.sign_out - User signs out
 */
export interface AuthSignOutEvent extends BaseEventProperties {
  platform: 'ios' | 'android' | 'web';
}

/**
 * auth.session_restored - Session restored from localStorage
 */
export interface AuthSessionRestoredEvent extends BaseEventProperties {
  platform: 'ios' | 'android' | 'web';
  success: boolean;
}

/**
 * auth.sign_up_start - User initiates sign-up flow
 */
export interface AuthSignUpStartEvent extends BaseEventProperties {
  platform: 'ios' | 'android' | 'web';
  method: 'email' | 'oauth';
  provider?: 'google' | 'strava';
}

/**
 * auth.sign_up_complete - User completes sign-up successfully
 */
export interface AuthSignUpCompleteEvent extends BaseEventProperties {
  platform: 'ios' | 'android' | 'web';
  method: 'email' | 'oauth';
  provider?: 'google' | 'strava';
  success: boolean;
}

// ============================================================================
// Guidance Lifecycle Events (Epic 4.12)
// ============================================================================

/**
 * guidance_start - User initiates "Get me there" guidance
 */
export interface GuidanceStartEvent extends BaseEventProperties {
  runId: string;
  runType: RunType;
  correlationId: string; // For request tracing
  originLat: number; // Redacted to 3 decimals
  originLng: number; // Redacted to 3 decimals
  targetLat: number; // Redacted to 3 decimals
  targetLng: number; // Redacted to 3 decimals
}

/**
 * route_request_sent - Route request sent to backend
 */
export interface GuidanceRouteRequestSentEvent extends BaseEventProperties {
  runId: string;
  correlationId: string;
  provider: 'ors';
  attemptNumber: number; // For retry tracking
  cacheAttempted: boolean;
}

/**
 * route_success - Route successfully received
 */
export interface GuidanceRouteSuccessEvent extends BaseEventProperties {
  runId: string;
  correlationId: string;
  provider: 'ors';
  durationMs: number; // Total request duration
  cached: boolean;
  cacheAge?: number; // If cached
  routeDistance: number; // In meters
  routeDuration: number; // In seconds
  breakerState: 'closed' | 'open' | 'half_open';
}

/**
 * route_fallback - Fallback to straight-line route
 */
export interface GuidanceRouteFallbackEvent extends BaseEventProperties {
  runId: string;
  correlationId: string;
  provider: 'ors';
  reason: 'timeout' | 'provider_error' | 'network_error' | 'circuit_breaker_open';
  durationMs: number;
  fallbackDistance: number; // In meters
  fallbackDuration: number; // In seconds
  breakerState: 'closed' | 'open' | 'half_open';
}

/**
 * route_error - Route request failed without fallback
 */
export interface GuidanceRouteErrorEvent extends BaseEventProperties {
  runId: string;
  correlationId: string;
  provider: 'ors';
  errorClass: 'timeout' | 'network' | 'provider' | 'validation' | 'authz';
  errorCode?: string;
  durationMs: number;
  breakerState: 'closed' | 'open' | 'half_open';
}

/**
 * guidance.throttle_denied - Request denied due to rate limit
 */
export interface GuidanceThrottleDeniedEvent extends BaseEventProperties {
  runId: string;
  throttleScope: 'user' | 'run' | 'ip';
  throttleClass: 'sustained' | 'burst';
  limit: number;
  windowSeconds: number;
}

/**
 * guidance.dedupe_suppressed - Request suppressed as duplicate
 */
export interface GuidanceDedupeSuppressedEvent extends BaseEventProperties {
  runId: string;
  reason: 'identical_request_within_dedupe_window' | 'near_identical_request_within_dedupe_window';
  timeSinceLastRequest: number; // milliseconds
  distanceFromLastOrigin: number; // meters
}

// ============================================================================
// Debug Events (opt-in, 10% sample or admin-only)
// ============================================================================

/**
 * debug.gps - GPS diagnostics
 */
export interface DebugGPSEvent extends BaseEventProperties {
  watchState: string;
  accuracy: number;
  distanceFilter?: number;
  pointsBuffered?: number;
}

/**
 * debug.navigation - Navigation timing
 */
export interface DebugNavigationEvent extends BaseEventProperties {
  transitionName: string;
  durationMs: number;
  droppedFrames?: number;
}

/**
 * debug.init - Backend initialization details
 */
export interface DebugInitEvent extends BaseEventProperties {
  result: string;
  reason: string;
  seed_version?: number;
  duration_ms?: number;
}

// ============================================================================
// Event Type Union
// ============================================================================

export type TelemetryEvent =
  // Loop events
  | { name: 'loop.start'; properties: LoopStartEvent }
  | { name: 'loop.checkpoint'; properties: LoopCheckpointEvent }
  | { name: 'loop.finish'; properties: LoopFinishEvent }
  | { name: 'loop.abandon'; properties: LoopAbandonEvent }
  | { name: 'loop.resume'; properties: LoopResumeEvent }
  // Event events
  | { name: 'event.create'; properties: EventCreateEvent }
  | { name: 'event.join'; properties: EventJoinEvent }
  | { name: 'event.leave'; properties: EventLeaveEvent }
  | { name: 'event.complete'; properties: EventCompleteEvent }
  // Admin events
  | { name: 'admin.loop.create'; properties: AdminLoopCreateEvent }
  | { name: 'admin.loop.update'; properties: AdminLoopUpdateEvent }
  | { name: 'admin.loop.delete'; properties: AdminLoopDeleteEvent }
  | { name: 'admin.user.set_role'; properties: AdminUserSetRoleEvent }
  | { name: 'admin.event.finalize'; properties: AdminEventFinalizeEvent }
  // Guidance events (Epic 4.12)
  | { name: 'guidance_start'; properties: GuidanceStartEvent }
  | { name: 'route_request_sent'; properties: GuidanceRouteRequestSentEvent }
  | { name: 'route_success'; properties: GuidanceRouteSuccessEvent }
  | { name: 'route_fallback'; properties: GuidanceRouteFallbackEvent }
  | { name: 'route_error'; properties: GuidanceRouteErrorEvent }
  | { name: 'guidance.throttle_denied'; properties: GuidanceThrottleDeniedEvent }
  | { name: 'guidance.dedupe_suppressed'; properties: GuidanceDedupeSuppressedEvent }
  // Auth events
  | { name: 'auth.sign_in'; properties: AuthSignInEvent }
  | { name: 'auth.sign_out'; properties: AuthSignOutEvent }
  | { name: 'auth.session_restored'; properties: AuthSessionRestoredEvent }
  | { name: 'auth.sign_up_start'; properties: AuthSignUpStartEvent }
  | { name: 'auth.sign_up_complete'; properties: AuthSignUpCompleteEvent }
  // Debug events
  | { name: 'debug.gps'; properties: DebugGPSEvent }
  | { name: 'debug.navigation'; properties: DebugNavigationEvent }
  | { name: 'debug.init'; properties: DebugInitEvent };

// ============================================================================
// Validation Helpers
// ============================================================================

/**
 * Validate that required fields are present in event properties
 * 
 * @param eventName - Event name
 * @param properties - Event properties
 * @throws {Error} if required fields are missing
 */
export function validateEventSchema(
  eventName: string,
  properties: Record<string, any>
): void {
  // Define required fields for each event type
  const requiredFields: Record<string, string[]> = {
    'loop.start': ['runType', 'loopId', 'runId'],
    'loop.checkpoint': ['runType', 'loopId', 'runId', 'checkpointId', 'sequenceNumber'],
    'loop.finish': ['runType', 'loopId', 'runId', 'totalTime', 'distance'],
    'loop.abandon': ['runType', 'loopId', 'runId', 'abandonedAt'],
    'loop.resume': ['runType', 'loopId', 'runId', 'pausedDuration'],
    'event.create': ['eventType', 'eventId', 'loopId', 'loopType'],
    'event.join': ['eventType', 'eventId', 'joinMethod'],
    'event.leave': ['eventType', 'eventId'],
    'event.complete': ['eventType', 'eventId', 'runId'],
    'admin.loop.create': ['loopType', 'loopId'],
    'admin.loop.update': ['loopType', 'loopId', 'changes'],
    'admin.loop.delete': ['loopType', 'loopId'],
    'admin.user.set_role': ['targetUserId', 'newRole'],
    'admin.event.finalize': ['eventId', 'participantCount'],
    'guidance_start': ['runId', 'runType', 'correlationId', 'originLat', 'originLng', 'targetLat', 'targetLng'],
    'route_request_sent': ['runId', 'correlationId', 'provider', 'attemptNumber', 'cacheAttempted'],
    'route_success': ['runId', 'correlationId', 'provider', 'durationMs', 'cached', 'routeDistance', 'routeDuration', 'breakerState'],
    'route_fallback': ['runId', 'correlationId', 'provider', 'reason', 'durationMs', 'fallbackDistance', 'fallbackDuration', 'breakerState'],
    'route_error': ['runId', 'correlationId', 'provider', 'errorClass', 'durationMs', 'breakerState'],
    'guidance.throttle_denied': ['runId', 'throttleScope', 'throttleClass', 'limit', 'windowSeconds'],
    'guidance.dedupe_suppressed': ['runId', 'reason', 'timeSinceLastRequest', 'distanceFromLastOrigin'],
    'auth.sign_in': ['platform', 'success'],
    'auth.sign_out': ['platform'],
    'auth.session_restored': ['platform', 'success'],
    'auth.sign_up_start': ['platform', 'method'],
    'auth.sign_up_complete': ['platform', 'method', 'success'],
    'debug.gps': ['watchState', 'accuracy'],
    'debug.navigation': ['transitionName', 'durationMs'],
    'debug.init': ['result', 'reason'],
  };
  
  const required = requiredFields[eventName];
  if (!required) {
    // Unknown event type - allow it but log warning
    if (import.meta.env.DEV) {
      console.warn(`[Telemetry] Unknown event type: ${eventName}`);
    }
    return;
  }
  
  // Check for missing required fields — warn instead of throw
  // Telemetry validation should never crash the app or block event tracking
  const missing = required.filter(field => !(field in properties));
  if (missing.length > 0) {
    if (import.meta.env.DEV) {
      console.warn(
        `[Telemetry] Missing fields for ${eventName}: ${missing.join(', ')} — event will still be sent`
      );
    }
  }
}

/**
 * Determine if an event should be sampled (debug events only)
 * 
 * Debug events are sampled at 10% OR always for admin users.
 * Normal events are always sent (100% sample rate).
 * 
 * @param eventName - Event name
 * @param isAdmin - Whether user is admin
 * @returns true if event should be sent
 */
export function shouldSampleEvent(eventName: string, isAdmin: boolean = false): boolean {
  // Normal events: always send
  if (!eventName.startsWith('debug.')) {
    return true;
  }
  
  // Debug events: 10% sample OR always for admins
  return isAdmin || Math.random() < 0.1;
}

/**
 * Get event category from event name
 * 
 * @param eventName - Event name (e.g., 'loop.start')
 * @returns Event category (e.g., 'loop')
 */
export function getEventCategory(eventName: string): string {
  return eventName.split('.')[0];
}

/**
 * Get event action from event name
 * 
 * @param eventName - Event name (e.g., 'loop.start')
 * @returns Event action (e.g., 'start')
 */
export function getEventAction(eventName: string): string {
  return eventName.split('.').slice(1).join('.');
}
